import { buildMonthDays } from '../../calendarGrid';
import { fetchWorkingHours, updateWorkingHours, fetchSpecialDays, saveSpecialDays, } from '../../../api/adminApi';
import { t, createScreenHeader, createBackButton, isUnauthorized } from './context';
import { createScheduleState, scheduleEffectiveValue, isScheduleValueValid, createScheduleFields, createScheduleActions, } from './scheduleEditor';
function dateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
// Two independent editors on one screen, each with its own Save/Reset pair:
// the recurring weekly hours and the date-specific overrides.
export function showWorkingHoursScreen(ctx) {
    ctx.screen.innerHTML = '';
    const header = createScreenHeader(t('admin.workingHours.title'));
    const content = document.createElement('div');
    content.className = 'admin__content';
    content.textContent = t('admin.workingHours.loading');
    const backButton = createBackButton(ctx, t('admin.workingHours.back'));
    ctx.screen.append(header, content, backButton);
    Promise.all([fetchWorkingHours(), fetchSpecialDays()])
        .then(([workingHours, specialDays]) => {
        content.innerHTML = '';
        renderWorkingHoursSection(ctx, content, workingHours);
        renderSpecialDaysSection(ctx, content, specialDays);
    })
        .catch((err) => {
        if (isUnauthorized(err)) {
            ctx.showLogin();
            return;
        }
        console.error('Failed to load working hours:', err);
        content.textContent = t('admin.workingHours.loadError');
    });
}
function renderWorkingHoursSection(ctx, container, initialDays) {
    const section = document.createElement('div');
    section.className = 'admin__schedule-section';
    const title = document.createElement('h2');
    title.className = 'admin__modal-title';
    title.textContent = t('admin.workingHours.weekly.title');
    section.appendChild(title);
    const state = createScheduleState(initialDays.map((day) => [day.weekday, { openTime: day.openTime, closeTime: day.closeTime, isClosed: day.isClosed }]));
    const bar = document.createElement('div');
    bar.className = 'admin__weekday-bar';
    const dayButtons = new Map();
    for (let weekday = 1; weekday <= 7; weekday += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'admin__weekday-btn';
        button.textContent = t(`booking.calendar.weekdays.${weekday - 1}`);
        button.addEventListener('click', () => {
            // Single active day at a time — switching to another day doesn't
            // lose its own pending edit (still in `pending`, shown via the
            // paler --dirty highlight), it just isn't the one being edited now.
            state.active.clear();
            state.active.add(weekday);
            fields.syncFieldsFromActive();
            renderBar();
        });
        dayButtons.set(weekday, button);
        bar.appendChild(button);
    }
    section.appendChild(bar);
    function renderBar() {
        for (const [weekday, button] of dayButtons) {
            button.classList.toggle('admin__weekday-btn--active', state.active.has(weekday));
            button.classList.toggle('admin__weekday-btn--dirty', state.pending.has(weekday));
        }
    }
    const fields = createScheduleFields(state, t('admin.workingHours.closedLabel'), () => {
        renderBar();
        actions.updateActionsState();
    });
    section.appendChild(fields.fieldsRow);
    const actions = createScheduleActions(() => state.pending.size);
    section.append(actions.errorEl, actions.actionsRow);
    actions.resetButton.addEventListener('click', () => {
        state.pending.clear();
        fields.syncFieldsFromActive();
        renderBar();
        actions.updateActionsState();
    });
    actions.saveButton.addEventListener('click', () => {
        actions.errorEl.hidden = true;
        // The server rejects a partial payload, so pending edits are merged over
        // serverState into all 7 rows before sending.
        const merged = [];
        for (let weekday = 1; weekday <= 7; weekday += 1) {
            merged.push({ weekday, ...scheduleEffectiveValue(state, weekday) });
        }
        if (!merged.every(isScheduleValueValid)) {
            actions.errorEl.textContent = t('admin.workingHours.invalidTimeError');
            actions.errorEl.hidden = false;
            return;
        }
        actions.saveButton.disabled = true;
        actions.saveButton.classList.add('is-loading');
        updateWorkingHours(merged)
            .then(() => {
            for (const day of merged) {
                state.serverState.set(day.weekday, { openTime: day.openTime, closeTime: day.closeTime, isClosed: day.isClosed });
            }
            state.pending.clear();
            renderBar();
            actions.updateActionsState();
            actions.saveButton.disabled = false;
            actions.saveButton.classList.remove('is-loading');
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                ctx.showLogin();
                return;
            }
            actions.errorEl.textContent = t('admin.workingHours.saveError');
            actions.errorEl.hidden = false;
            actions.saveButton.disabled = false;
            actions.saveButton.classList.remove('is-loading');
        });
    });
    renderBar();
    actions.updateActionsState();
    container.appendChild(section);
}
function renderSpecialDaysSection(ctx, container, initialDays) {
    const section = document.createElement('div');
    section.className = 'admin__schedule-section';
    const title = document.createElement('h2');
    title.className = 'admin__modal-title';
    title.textContent = t('admin.workingHours.specialDays.title');
    section.appendChild(title);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let viewYear = today.getFullYear();
    let viewMonth = today.getMonth();
    const state = createScheduleState(initialDays.map((day) => [day.date, { openTime: day.openTime, closeTime: day.closeTime, isClosed: day.isClosed }]));
    const calendar = document.createElement('div');
    calendar.className = 'admin__cal';
    const calHead = document.createElement('div');
    calHead.className = 'admin__cal-head';
    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'admin__cal-nav';
    prevButton.setAttribute('aria-label', t('admin.workingHours.specialDays.prevMonthLabel'));
    prevButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'admin__cal-nav';
    nextButton.setAttribute('aria-label', t('admin.workingHours.specialDays.nextMonthLabel'));
    nextButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const monthLabel = document.createElement('span');
    monthLabel.className = 'admin__cal-month';
    calHead.append(prevButton, monthLabel, nextButton);
    const weekdaysRow = document.createElement('div');
    weekdaysRow.className = 'admin__cal-weekdays';
    for (let day = 0; day < 7; day += 1) {
        const cell = document.createElement('span');
        cell.className = 'admin__cal-weekday';
        cell.textContent = t(`booking.calendar.weekdays.${day}`);
        weekdaysRow.appendChild(cell);
    }
    const calGrid = document.createElement('div');
    calGrid.className = 'admin__cal-grid';
    calendar.append(calHead, weekdaysRow, calGrid);
    section.appendChild(calendar);
    // Reuses buildMonthDays for month-grid geometry (the same helper the booking
    // calendar uses) but keeps its own .admin__cal-* classes and selection logic
    // for the pending-state highlighting the booking calendar has no use for.
    function renderCalendarGrid() {
        monthLabel.textContent = `${t(`booking.calendar.months.${viewMonth}`)} ${viewYear}`;
        calGrid.innerHTML = '';
        for (const date of buildMonthDays(viewYear, viewMonth)) {
            if (!date) {
                const empty = document.createElement('span');
                empty.className = 'admin__cal-day admin__cal-day--empty';
                calGrid.appendChild(empty);
                continue;
            }
            const key = dateKey(date);
            const isPast = date < today;
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'admin__cal-day';
            button.textContent = String(date.getDate());
            if (isSameDay(date, today))
                button.classList.add('admin__cal-day--today');
            if (state.active.has(key))
                button.classList.add('admin__cal-day--active');
            if (state.pending.has(key))
                button.classList.add('admin__cal-day--dirty');
            if (isPast) {
                button.classList.add('admin__cal-day--disabled');
                button.disabled = true;
            }
            else {
                button.addEventListener('click', () => {
                    // Single active date at a time — same model as the weekday bar:
                    // switching dates doesn't lose the previous one's pending edit,
                    // it just isn't the one being edited now (paler --dirty highlight).
                    state.active.clear();
                    state.active.add(key);
                    fields.syncFieldsFromActive();
                    renderCalendarGrid();
                });
            }
            calGrid.appendChild(button);
        }
    }
    prevButton.addEventListener('click', () => {
        viewMonth -= 1;
        if (viewMonth < 0) {
            viewMonth = 11;
            viewYear -= 1;
        }
        renderCalendarGrid();
    });
    nextButton.addEventListener('click', () => {
        viewMonth += 1;
        if (viewMonth > 11) {
            viewMonth = 0;
            viewYear += 1;
        }
        renderCalendarGrid();
    });
    const fields = createScheduleFields(state, t('admin.workingHours.specialDays.dayOffLabel'), () => {
        renderCalendarGrid();
        actions.updateActionsState();
    });
    section.appendChild(fields.fieldsRow);
    const actions = createScheduleActions(() => state.pending.size);
    section.append(actions.errorEl, actions.actionsRow);
    actions.resetButton.addEventListener('click', () => {
        state.pending.clear();
        fields.syncFieldsFromActive();
        renderCalendarGrid();
        actions.updateActionsState();
    });
    actions.saveButton.addEventListener('click', () => {
        actions.errorEl.hidden = true;
        // Unlike the weekly section, only edited dates are sent — the server
        // upserts exactly the dates in the payload and leaves the rest alone.
        const changed = [...state.pending.entries()].map(([date, value]) => ({ date, ...value }));
        if (!changed.every(isScheduleValueValid)) {
            actions.errorEl.textContent = t('admin.workingHours.invalidTimeError');
            actions.errorEl.hidden = false;
            return;
        }
        actions.saveButton.disabled = true;
        actions.saveButton.classList.add('is-loading');
        saveSpecialDays(changed)
            .then(() => {
            for (const day of changed) {
                state.serverState.set(day.date, { openTime: day.openTime, closeTime: day.closeTime, isClosed: day.isClosed });
            }
            state.pending.clear();
            renderCalendarGrid();
            actions.updateActionsState();
            actions.saveButton.disabled = false;
            actions.saveButton.classList.remove('is-loading');
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                ctx.showLogin();
                return;
            }
            actions.errorEl.textContent = t('admin.workingHours.saveError');
            actions.errorEl.hidden = false;
            actions.saveButton.disabled = false;
            actions.saveButton.classList.remove('is-loading');
        });
    });
    renderCalendarGrid();
    actions.updateActionsState();
    container.appendChild(section);
}
